<template>
    
    <!-- Locked Status Warning Banner -->
    <div v-if="isFormLocked" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
        <i class="fas fa-lock me-2"></i>
        <div>
            <strong>This request is locked.</strong> 
            The status is set to "{{ currentStatusName }}" which prevents editing. 
            Change the status to make edits to this request.
        </div>
    </div>
    
    <div class="accordion" id="jobFormAccordion">
        
        <!-- General Information Section (Combined: Basic Info + Status/Scheduling + Payment) -->
        <div class="accordion-item">
            <h2 class="accordion-header">
                <button 
                    class="accordion-button accordion-button-sm" 
                    :class="{ collapsed: !sections.generalInfo }"
                    type="button" 
                    @click="toggleSection('generalInfo')"
                >
                    Request Information
                </button>
            </h2>
            <div class="accordion-collapse collapse" :class="{ show: sections.generalInfo }">
                <div class="accordion-body">
                    <!-- Basic Information -->
                    <div class="row">
                        <div class="col-6">
                            <label class="d-block">
                                Choose Client
                            </label>
                            <client-ac v-model="model.Customer" inputclass="form-control form-control-sm" :disabled="isFormLocked" :readonly="isFormLocked"></client-ac>
                        </div>
                        <div class="col-6">
                            <tech-select 
                                label="Assign Owner"
                                :active="1" 
                                v-model="model.Technician"
                                :disabled="isFormLocked"
                            ></tech-select>
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col-6">
                            <kt-input
                                type="text"
                                label="Request Description" 
                                inputclass="form-control form-control-sm"
                                v-model="model.RequestDetailedDescription"
                                placeholder="Request Description"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                        <div class="col-6">
                            <kt-input
                                type="text"
                                label="Location" 
                                inputclass="form-control form-control-sm"
                                v-model="model.Location"
                                placeholder="Location"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col">
                            <kt-input
                                type="textarea"
                                label="Task Detail" 
                                inputclass="form-control form-control-sm"
                                v-model="model.TaskDetail"
                                placeholder="Request / Task Details"
                                :rows="3"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>

                    <!-- Status & Scheduling -->
                    <div class="row mt-3">
                        <div class="col-12">
                            <h6 class="text-muted mb-2">Status & Scheduling</h6>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <kt-select
                                v-model="model.RequestStatus"
                                :options="statusList"
                                label="Status"
                                label-key="label"
                                value-key="value"
                                placeholder="Choose Status"
                            ></kt-select>
                        </div>
                        <div class="col-6">
                            <kt-select
                                v-model="model.Priority"
                                :options="priorityList"
                                label="Priority"
                                label-key="label"
                                value-key="value"
                                placeholder="Choose Priority"
                                :disabled="isFormLocked"
                            ></kt-select>
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col-4">
                            <kt-input
                                type="date"
                                label="Due Date"
                                inputclass="form-control form-control-sm"
                                v-model="model.DueDate"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                        <div class="col-4">
                            <kt-input
                                type="date"
                                label="Completion Date"
                                inputclass="form-control form-control-sm"
                                v-model="model.CompletionDate"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                        <div class="col-4">
                            <kt-input
                                type="number"
                                label="Progress %"
                                inputclass="form-control form-control-sm"
                                v-model="model.Progress"
                                placeholder="0-100"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>

                    <!-- Payment Information -->
                    <div class="row mt-3">
                        <div class="col-12">
                            <h6 class="text-muted mb-2">Payment Information</h6>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <kt-input
                                type="date"
                                label="Paid On"
                                inputclass="form-control form-control-sm"
                                v-model="model.PaidOn"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                        <div class="col-6">
                            <kt-input
                                type="textarea"
                                label="Billing Notes"
                                inputclass="form-control form-control-sm"
                                v-model="model.BillingNotes"
                                placeholder="Billing Notes"
                                :rows="2"
                                :disabled="isFormLocked"
                                :readonly="isFormLocked"
                            ></kt-input>
                        </div>
                    </div>

                    <!-- Information Tabs (Project, Estimates, Invoice, Service Report) -->
                    <div class="row mt-4">
                        <div class="col">
                            <ul class="nav nav-tabs" role="tablist">
                                <li class="nav-item" role="presentation">
                                    <button 
                                        class="nav-link" 
                                        :class="{ active: activeInfoTab === 'project' }"
                                        @click="activeInfoTab = 'project'"
                                        type="button"
                                    >
                                        Project Information
                                    </button>
                                </li>
                                <li class="nav-item" role="presentation">
                                    <button 
                                        class="nav-link" 
                                        :class="{ active: activeInfoTab === 'estimates' }"
                                        @click="activeInfoTab = 'estimates'"
                                        type="button"
                                    >
                                        Estimates
                                    </button>
                                </li>
                                <li class="nav-item" role="presentation">
                                    <button 
                                        class="nav-link" 
                                        :class="{ active: activeInfoTab === 'invoice' }"
                                        @click="activeInfoTab = 'invoice'"
                                        type="button"
                                    >
                                        Invoice Information
                                    </button>
                                </li>
                                <li class="nav-item" role="presentation">
                                    <button 
                                        class="nav-link" 
                                        :class="{ active: activeInfoTab === 'service' }"
                                        @click="activeInfoTab = 'service'"
                                        type="button"
                                    >
                                        Service Report
                                    </button>
                                </li>
                            </ul>

                            <div class="tab-content border border-top-0 p-3 bg-light">
                                <!-- Project Information Tab -->
                                <div v-show="activeInfoTab === 'project'">
                                    <div class="row">
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Request Type"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.RequestType"
                                                placeholder="Request Type"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Requestor"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.Requestor"
                                                placeholder="Requestor Name"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <div class="form-check mt-4">
                                                <input 
                                                    class="form-check-input" 
                                                    type="checkbox" 
                                                    v-model="model.Project"
                                                    id="projectCheckbox"
                                                    :disabled="isFormLocked"
                                                >
                                                <label class="form-check-label" for="projectCheckbox">
                                                    Is Project
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row mt-2">
                                        <div class="col-6">
                                            <kt-input
                                                type="text"
                                                label="Project Folder"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.ProjectFolder"
                                                placeholder="Project Folder Path"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-6">
                                            <kt-input
                                                type="text"
                                                label="Work Time"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.WorkTime"
                                                placeholder="Work Time"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>
                                </div>

                                <!-- Estimates Tab -->
                                <div v-show="activeInfoTab === 'estimates'">
                                    <div class="row">
                                        <div class="col-4">
                                            <kt-input
                                                type="number"
                                                label="Estimated Amount"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.EstimatedAmount"
                                                placeholder="0.00"
                                                step="0.01"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Estimate Number"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.EstimateNumber"
                                                placeholder="Estimate #"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Time Estimate"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.TimeEstimate"
                                                placeholder="Time Estimate"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>
                                </div>

                                <!-- Invoice Information Tab -->
                                <div v-show="activeInfoTab === 'invoice'">
                                    <div class="row">
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Invoice Number"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoiceNumber"
                                                placeholder="Invoice #"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="date"
                                                label="Invoiced On"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoicedOn"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="number"
                                                label="Invoice Total"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoiceTotal"
                                                placeholder="0.00"
                                                step="0.01"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>

                                    <div class="row mt-2">
                                        <div class="col">
                                            <kt-input
                                                type="textarea"
                                                label="Invoice Description"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoiceDescription"
                                                placeholder="Invoice Description"
                                                :rows="2"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>

                                    <!-- Secondary Invoice -->
                                    <div class="row mt-3">
                                        <div class="col-12">
                                            <h6 class="text-muted mb-2">Secondary Invoice</h6>
                                        </div>
                                    </div>

                                    <div class="row mt-2">
                                        <div class="col-4">
                                            <kt-input
                                                type="text"
                                                label="Invoice Number 2"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoiceNumber2"
                                                placeholder="Invoice #2"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="date"
                                                label="Invoiced On 2"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoicedOn2"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                        <div class="col-4">
                                            <kt-input
                                                type="number"
                                                label="Should Bill Total"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.ShouldBillTotal"
                                                placeholder="0.00"
                                                step="0.01"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>

                                    <div class="row mt-2">
                                        <div class="col">
                                            <kt-input
                                                type="textarea"
                                                label="Invoice Description 2"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.InvoiceDescription2"
                                                placeholder="Invoice Description 2"
                                                :rows="2"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>
                                </div>

                                <!-- Service Report Tab -->
                                <div v-show="activeInfoTab === 'service'">
                                    <div class="row">
                                        <div class="col">
                                            <kt-input
                                                type="textarea"
                                                label="Service Report"
                                                inputclass="form-control form-control-sm"
                                                v-model="model.ServiceReport"
                                                placeholder="Service Report Details"
                                                :rows="4"
                                                :disabled="isFormLocked"
                                                :readonly="isFormLocked"
                                            ></kt-input>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

    </div>

    <!-- Related Records Tabs (Tasks, Parts, Expenses, Mileage, Assets) -->
    <div class="row mt-4" v-if="model.Id">
        <div class="col">
            <ul class="nav nav-tabs" role="tablist">
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeTab === 'time' }"
                        @click="activeTab = 'time'"
                        type="button"
                    >
                        Tasks
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeTab === 'parts' }"
                        @click="activeTab = 'parts'"
                        type="button"
                    >
                        Parts
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeTab === 'expenses' }"
                        @click="activeTab = 'expenses'"
                        type="button"
                    >
                        Expenses
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeTab === 'mileage' }"
                        @click="activeTab = 'mileage'"
                        type="button"
                    >
                        Mileage
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeTab === 'assets' }"
                        @click="activeTab = 'assets'"
                        type="button"
                    >
                        Assets
                    </button>
                </li>
            </ul>

            <div class="tab-content border border-top-0 p-3 bg-light">
                <!-- Tasks Tab -->
                <div v-show="activeTab === 'time'">
                    <time-list 
                        :request-id="model.Id"
                        :client-id="model.Customer"
                        :tech-id="model.Technician"
                        :hide-filters="true"
                        :embedded="true"
                        :disable-actions="isFormLocked"
                    ></time-list>
                </div>

                <!-- Parts Tab -->
                <div v-show="activeTab === 'parts'">
                    <part-list 
                        :request-id="model.Id"
                        :client-id="model.Customer"
                        :tech-id="model.Technician"
                        :hide-filters="true"
                        :embedded="true"
                        :disable-actions="isFormLocked"
                    ></part-list>
                </div>

                <!-- Expenses Tab -->
                <div v-show="activeTab === 'expenses'">
                    <expense-list 
                        :request-id="model.Id"
                        :client-id="model.Customer"
                        :tech-id="model.Technician"
                        :hide-filters="true"
                        :embedded="true"
                        :disable-actions="isFormLocked"
                    ></expense-list>
                </div>

                <!-- Mileage Tab -->
                <div v-show="activeTab === 'mileage'">
                    <mileage-list 
                        :request-id="model.Id"
                        :client-id="model.Customer"
                        :tech-id="model.Technician"
                        :hide-filters="true"
                        :embedded="true"
                        :disable-actions="isFormLocked"
                    ></mileage-list>
                </div>

                <!-- Assets Tab -->
                <div v-show="activeTab === 'assets'">
                    <div v-if="isFormLocked" class="alert alert-warning">
                        <i class="fas fa-lock me-2"></i>
                        File uploads are disabled when the job is locked.
                    </div>
                    <kt-upload 
                        v-else
                        @process="processFiles($event)"
                        multiple="1"    
                    ></kt-upload>

                    <table class="table table-sm table-striped mt-3" v-if="files.length">
                        <thead>
                            <tr>
                                <th style="width: 80px">Actions</th>
                                <th>File Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="file in files" :key="file.Id">
                                <td>
                                    <button class="btn btn-sm btn-outline-danger" @click="showDelete(file)">
                                        <i class="fa fa-trash"></i>
                                    </button>
                                </td>
                                <td>
                                    <a :href="'https://radarapp.7thleveltech.com/v1/Data/Download/' + file.Id" target="_blank">
                                        {{ file.Title }}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="!files.length" class="alert alert-secondary mt-3">
                        <i class="fa fa-file"></i> No files uploaded yet.
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Alert from "@/services/Alert";
import Helper from "@/services/Helper";
import { useJobStatusStore } from "@/stores/JobStatusStore";

import Attic from "@/services/Attic.js";
import ClientAc from "@/components/Clients/ClientAc.vue";
import TechSelect from "@/components/Techs/TechSelect.vue";
import KtInput from "@/components/UI/KtInput.vue";
import KtSelect from "@/components/UI/KtSelect.vue";
import KtUpload from "@/components/UI/KtUpload.vue";
import TimeList from "@/components/Time/TimeList.vue";
import PartList from "@/components/Parts/PartList.vue";
import ExpenseList from "@/components/Expenses/ExpenseList.vue";
import MileageList from "@/components/Mileage/MileageList.vue";

const statusList = Attic.JobStatusList;
const priorityList = Attic.PriorityList;

const _ = ref({});

// Closed statuses that lock the form
const closedStatuses = ref([]);

// Load closed statuses
const loadClosedStatuses = async () => {
    try {
        closedStatuses.value = await ApiService.loadClosedStatuses(true);
    } catch (error) {
        console.error('Failed to load closed statuses:', error);
    }
};

// Get job status store to access status details
const jobStatusStore = useJobStatusStore();
const { list: JobStatusList } = storeToRefs(jobStatusStore);

// Computed property to check if form should be locked
const isFormLocked = computed(() => {
    if (!model.value?.Id || !model.value?.RequestStatus) {
        return false; // New job or no status, not locked
    }
    
    if (!JobStatusList.value || Object.keys(JobStatusList.value).length === 0) {
        return false; // Store not loaded yet
    }
    
    const currentStatus = JobStatusList.value[model.value.RequestStatus];
    if (!currentStatus || !currentStatus.Status) {
        return false;
    }
    
    return closedStatuses.value.some(closedStatus => 
        closedStatus.toLowerCase() === currentStatus.Status.toLowerCase()
    );
});

// Get current status name for display
const currentStatusName = computed(() => {
    if (!model.value?.RequestStatus) return '';
    if (!JobStatusList.value) return '';
    const status = JobStatusList.value[model.value.RequestStatus];
    return status?.Status || '';
});

// Active tab state for bottom tabs
const activeTab = ref('time');

// Active tab state for info tabs (Project, Estimates, Invoice, Service Report)
const activeInfoTab = ref('project');

// Section visibility state
const sections = ref({
    generalInfo: true
});

// Toggle section visibility
const toggleSection = (sectionName) => {
    sections.value[sectionName] = !sections.value[sectionName];
};

const unwatch = [];
const files = ref([]);

const route = useRoute();
const model = defineModel({
    type: Object,
    default: { }
});

const processFiles = async (files) =>
{
    for (let i = 0; i < files.length; i++)
    {
        let file = files[i];
        var dta = new FormData();
        dta.append("File", file);
        dta.append("Type", "request");
        dta.append("RequestId", model.value.Id); 
        // do we need anything else ...
        await Api.saveFile(dta);   
    }
    
    await load();
};

const load = async () =>
{
    files.value = await Api.loadRequestFiles(model.value.Id);
};

const showDelete = async (file) =>
{
    Alert.confirm("Are you sure you want to remove that file?", async () => {
        await Api.deleteFile(file.Id);
        load();
    });
}


onMounted(async () =>
{
    // Load closed statuses
    await loadClosedStatuses();
    
    // Ensure job status store is loaded
    await jobStatusStore.reload();
    
    unwatch.push(
        watch(() =>
        {
            return model.value?.Id;
        }, (nv, ov) => {
            load();
        })
    );
});

onUnmounted(() =>
{
    unwatch.forEach((un) => {
        un();
    });
});

</script>

<style scoped>
/* Grey background for accordion bodies - classic form builder look */
.accordion-body {
    background-color: #f8f9fa;
}

/* Keep accordion items with white background */
.accordion-item {
    background-color: #ffffff;
}

.accordion-button {
    background-color: #e0e0e0;
    color: #111;
}

.accordion-button:hover {
    background-color: #e0e0e0;
    color: #111;
}
</style>
